import React from 'react';
import { useInput } from 'react-admin';
import {
  Select,
  MenuItem,
  ListSubheader,
  InputLabel,
  FormControl,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  formControl: {
    minWidth: 120,
    marginBottom: 8,
  },
});

interface PhaseSelectInputProps {
  source: string;
  label?: string;
  choices?: any[];
  helperText?: string;
  applyPriorityFilter?: boolean;
  applyDrywallAndPaintFilter?: boolean;
  [key: string]: any;
}

export const PhaseSelectInput: React.FC<PhaseSelectInputProps> = props => {
  const {
    choices = [],
    label = 'Phase',
    applyPriorityFilter = true,
    applyDrywallAndPaintFilter = false,
    input: inputProp,
  } = props;
  const classes = useStyles();

  // Use provided input prop if available, otherwise use useInput hook
  let value, onChange;
  if (inputProp) {
    value = inputProp.value;
    onChange = inputProp.onChange;
  } else {
    const inputFromHook = useInput(props);
    value = inputFromHook.input.value;
    onChange = inputFromHook.input.onChange;
  }

  // Check if user has TN Reports access
  const showTNReports = localStorage.getItem('showTNReports') === 'true';

  // Allowed phaseTypes for DrywallAndPaint when filter is applied
  const allowedDrywallAndPaintPhases = [
    'Hang',
    'Finish',
    'Prime',
    'Trim1',
    'InteriorPaint',
    'Trim2',
    'FinalPaint',
  ];

  // Group phases by phaseStructureType
  // Drywall: filter by priority 0-500 (excludes Bump phases which are 600+) if applyPriorityFilter is true
  const drywallPhases = choices
    .filter(c => {
      if (c.phaseStructureType !== 'Drywall') return false;
      if (applyPriorityFilter) {
        return c.priority >= 0 && c.priority <= 500;
      }
      return true;
    })
    .sort((a, b) => a.priority - b.priority);

  // DrywallAndPaint: show all phases or filtered phases based on applyDrywallAndPaintFilter
  const drywallAndPaintPhases = choices
    .filter(c => {
      if (c.phaseStructureType !== 'DrywallAndPaint') return false;
      if (applyDrywallAndPaintFilter) {
        return allowedDrywallAndPaintPhases.includes(c.phaseType);
      }
      return true;
    })
    .sort((a, b) => a.priority - b.priority);

  return (
    <FormControl variant="filled" className={classes.formControl} fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        displayEmpty
        variant="filled"
        margin="dense"
        fullWidth
      >
        <MenuItem value="">All</MenuItem>

        {/* Show Drywall phases */}
        {showTNReports && drywallPhases.length > 0 && (
          <ListSubheader key="drywall-header">Drywall</ListSubheader>
        )}
        {drywallPhases.map(phase => (
          <MenuItem key={phase.id} value={phase.id}>
            {phase.name}
          </MenuItem>
        ))}

        {/* Show DrywallAndPaint phases only if showTNReports is true */}
        {showTNReports && drywallAndPaintPhases.length > 0 && (
          <ListSubheader key="paint-header">Drywall and Paint</ListSubheader>
        )}
        {showTNReports && drywallAndPaintPhases.map(phase => (
          <MenuItem key={phase.id} value={phase.id}>
            {phase.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
